import React from 'react';

import { ReactComponent as ATFlag } from '../assets/images/flags/AT.svg';
import { ReactComponent as BEFlag } from '../assets/images/flags/BE.svg';
import { ReactComponent as CHFlag } from '../assets/images/flags/CH.svg';
import { ReactComponent as CZFlag } from '../assets/images/flags/CZ.svg';
import { ReactComponent as DEFlag } from '../assets/images/flags/DE.svg';
import { ReactComponent as DKFlag } from '../assets/images/flags/DK.svg';
import { ReactComponent as ESFlag } from '../assets/images/flags/ES.svg';
import { ReactComponent as FRFlag } from '../assets/images/flags/FR.svg';
import { ReactComponent as GBFlag } from '../assets/images/flags/GB.svg';
import { ReactComponent as HUFlag } from '../assets/images/flags/HU.svg';
import { ReactComponent as ITFlag } from '../assets/images/flags/IT.svg';
import { ReactComponent as LIFlag } from '../assets/images/flags/LI.svg';
import { ReactComponent as LUFlag } from '../assets/images/flags/LU.svg';
import { ReactComponent as FallbackFlag } from '../assets/images/flags/NB.svg';
import { ReactComponent as NLFlag } from '../assets/images/flags/NL.svg';
import { ReactComponent as PLFlag } from '../assets/images/flags/PL.svg';
import { ReactComponent as PTFlag } from '../assets/images/flags/PT.svg';
import { ReactComponent as ROFlag } from '../assets/images/flags/RO.svg';
import { ReactComponent as SIFlag } from '../assets/images/flags/SI.svg';
import { ReactComponent as SKFlag } from '../assets/images/flags/SK.svg';
import { getHoffmanEnvironment } from './windowService';

const { SUPPORTED_LOCALES } = getHoffmanEnvironment();

const envLocales = SUPPORTED_LOCALES && SUPPORTED_LOCALES.split(',').map((locale) => locale.trim());

export const isValidEnvLocale = (locale: string) =>
  locale && (!envLocales || envLocales.includes(locale));

interface SupportedLocaleSetting {
  country: AllSupportedCountries;
  name: string;
  image: any;
  defaultLanguage: SupportedLocale;
}

export enum SupportedLocale {
  default = 'en', // please keep it, required for memoization in 'react-translate-plus' in case of bad requests from localisation service

  CS_CZ = 'cs-CZ',
  DA_DK = 'da-DK',
  DE_DE = 'de-DE',
  EN_GB = 'en-GB',
  ES_ES = 'es-ES',
  FI_FI = 'fi-FI',
  FR_FR = 'fr-FR',
  HR_HR = 'hr-HR', // Croatian (Croatia)
  HU_HU = 'hu-HU',
  IT_IT = 'it-IT',
  LT_LT = 'lt-LT',
  NL_NL = 'nl-NL',
  PL_PL = 'pl-PL',
  RO_RO = 'ro-RO',
  RU_RU = 'ru-RU',
  SL_SI = 'sl-SI',
  SV_SE = 'sv-SE', // sweden
  ZH_CN = 'zh-CN',
}

export enum AllSupportedCountries {
  AT = 'AT',
  BE = 'BE',
  CH = 'CH',
  CZ = 'CZ',
  DE = 'DE',
  DK = 'DK',
  GB = 'GB',
  ES = 'ES',
  FR = 'FR',
  HU = 'HU',
  LI = 'LI',
  LU = 'LU',
  IT = 'IT',
  NL = 'NL',
  PL = 'PL',
  PT = 'PT',
  RO = 'RO',
  SI = 'SI',
  SK = 'SK',
}

export const allSupportedLocaleSetting = new Map<string, SupportedLocaleSetting>([
  [
    AllSupportedCountries.AT,
    {
      country: AllSupportedCountries.AT,
      defaultLanguage: SupportedLocale.DE_DE,
      image: ATFlag,
      name: 'Österreich',
    },
  ],
  [
    AllSupportedCountries.BE,
    {
      country: AllSupportedCountries.BE,
      defaultLanguage: SupportedLocale.EN_GB,
      image: BEFlag,
      name: 'Belgium',
    },
  ],
  [
    AllSupportedCountries.CZ,
    {
      country: AllSupportedCountries.CZ,
      defaultLanguage: SupportedLocale.CS_CZ,
      image: CZFlag,
      name: 'Ceska republika',
    },
  ],
  [
    AllSupportedCountries.CH,
    {
      country: AllSupportedCountries.CH,
      defaultLanguage: SupportedLocale.DE_DE,
      image: CHFlag,
      name: 'Switzerland',
    },
  ],
  [
    AllSupportedCountries.DE,
    {
      country: AllSupportedCountries.DE,
      defaultLanguage: SupportedLocale.DE_DE,
      image: DEFlag,
      name: 'Deutschland',
    },
  ],
  [
    AllSupportedCountries.DK,
    {
      country: AllSupportedCountries.DK,
      defaultLanguage: SupportedLocale.DA_DK,
      image: DKFlag,
      name: 'Danmark',
    },
  ],
  [
    AllSupportedCountries.GB,
    {
      country: AllSupportedCountries.GB,
      defaultLanguage: SupportedLocale.EN_GB,
      image: GBFlag,
      name: 'Great Britain',
    },
  ],
  [
    AllSupportedCountries.ES,
    {
      country: AllSupportedCountries.ES,
      defaultLanguage: SupportedLocale.ES_ES,
      image: ESFlag,
      name: 'España',
    },
  ],
  [
    AllSupportedCountries.FR,
    {
      country: AllSupportedCountries.FR,
      defaultLanguage: SupportedLocale.FR_FR,
      image: FRFlag,
      name: 'France',
    },
  ],
  [
    AllSupportedCountries.HU,
    {
      country: AllSupportedCountries.HU,
      defaultLanguage: SupportedLocale.HU_HU,
      image: HUFlag,
      name: 'Magyarország',
    },
  ],
  [
    AllSupportedCountries.LI,
    {
      country: AllSupportedCountries.LI,
      defaultLanguage: SupportedLocale.DE_DE,
      image: LIFlag,
      name: 'Liechenstein',
    },
  ],
  [
    AllSupportedCountries.LU,
    {
      country: AllSupportedCountries.LU,
      defaultLanguage: SupportedLocale.EN_GB,
      image: LUFlag,
      name: 'Luxembourg',
    },
  ],
  [
    AllSupportedCountries.IT,
    {
      country: AllSupportedCountries.IT,
      defaultLanguage: SupportedLocale.IT_IT,
      image: ITFlag,
      name: 'Italia',
    },
  ],
  [
    AllSupportedCountries.NL,
    {
      country: AllSupportedCountries.NL,
      defaultLanguage: SupportedLocale.NL_NL,
      image: NLFlag,
      name: 'Nederland',
    },
  ],
  [
    AllSupportedCountries.PL,
    {
      country: AllSupportedCountries.PL,
      defaultLanguage: SupportedLocale.PL_PL,
      image: PLFlag,
      name: 'Polska',
    },
  ],
  [
    AllSupportedCountries.PT,
    {
      country: AllSupportedCountries.PT,
      defaultLanguage: SupportedLocale.ES_ES,
      image: PTFlag,
      name: 'Portugal',
    },
  ],
  [
    AllSupportedCountries.RO,
    {
      country: AllSupportedCountries.RO,
      defaultLanguage: SupportedLocale.RO_RO,
      image: ROFlag,
      name: 'România',
    },
  ],
  [
    AllSupportedCountries.SI,
    {
      country: AllSupportedCountries.SI,
      defaultLanguage: SupportedLocale.SL_SI,
      image: SIFlag,
      name: 'Slovenija',
    },
  ],
  [
    AllSupportedCountries.SK,
    {
      country: AllSupportedCountries.SK,
      defaultLanguage: SupportedLocale.CS_CZ,
      image: SKFlag,
      name: 'Slovenska republika',
    },
  ],
]);

export const getCountryImage = (country: string, dimension?: string) => {
  let countryWithImage = allSupportedLocaleSetting.get(country);
  if (!countryWithImage) {
    //use default if unknown country is provided
    return React.createElement(FallbackFlag, {
      width: dimension ? dimension : '20',
      height: dimension ? dimension : '20',
    });
  }
  return React.createElement(countryWithImage?.image, {
    width: dimension ? dimension : '20',
    height: dimension ? dimension : '20',
  });
};

export const allLocales = new Map<SupportedLocale, string>([
  [SupportedLocale.CS_CZ, 'Čeština'],
  [SupportedLocale.DA_DK, 'Dansk'],
  [SupportedLocale.DE_DE, 'Deutsch'],
  [SupportedLocale.EN_GB, 'English'],
  [SupportedLocale.ES_ES, 'Español'],
  [SupportedLocale.FI_FI, 'Suomi'],
  [SupportedLocale.FR_FR, 'Français'],
  [SupportedLocale.HR_HR, 'Hrvatski'],
  [SupportedLocale.HU_HU, 'Magyar'],
  [SupportedLocale.IT_IT, 'Italiano'],
  [SupportedLocale.LT_LT, 'Lietuvis'],
  [SupportedLocale.NL_NL, 'Nederlands'],
  [SupportedLocale.PL_PL, 'Polski'],
  [SupportedLocale.RO_RO, 'Română'],
  [SupportedLocale.RU_RU, 'Русский'],
  [SupportedLocale.SL_SI, 'Slovenski'],
  [SupportedLocale.SV_SE, 'Svenska'],
  [SupportedLocale.ZH_CN, '中文'],
]);

export const supportedLocales = new Map<SupportedLocale, string>(
  [...allLocales.entries()].filter(([locale]) => isValidEnvLocale(locale)) as [
    SupportedLocale,
    string,
  ][],
);

export const getDefaultAndSelectedSupportedLocales = (
  language: string,
  country: string,
): Map<SupportedLocale, string> => {
  const defaults = new Map<SupportedLocale, string>();

  const forLanguage = supportedLocales?.get(getLocaleForLanguage(language));
  if (forLanguage) {
    defaults.set(getLocaleForLanguage(language), forLanguage);
  }
  const forCountry = allSupportedLocaleSetting?.get(country)?.defaultLanguage;
  if (forCountry && isValidEnvLocale(forCountry)) {
    const supportedForCountry = supportedLocales.get(forCountry);
    supportedForCountry && defaults.set(forCountry, supportedForCountry);
  }
  const forDefault = supportedLocales.get(SupportedLocale.EN_GB);
  if (forDefault) {
    defaults.set(SupportedLocale.EN_GB, forDefault);
  }
  return new Map([...defaults].sort());
};

export const getSupportedLocalesWithoutDefaultAndSelected = (
  language: string,
  country: string,
): Map<SupportedLocale, string> => {
  const filteredLocales = new Map(supportedLocales);
  filteredLocales.delete(SupportedLocale.EN_GB);
  if (allSupportedLocaleSetting?.get(country)?.defaultLanguage) {
    filteredLocales.delete(
      allSupportedLocaleSetting?.get(country)?.defaultLanguage ?? SupportedLocale.EN_GB,
    );
  }
  filteredLocales.delete(getLocaleForLanguage(language));
  return filteredLocales;
};

export const defaultLocalesForLanguages = {
  cs: SupportedLocale.CS_CZ,
  da: SupportedLocale.DA_DK,
  de: SupportedLocale.DE_DE,
  en: SupportedLocale.EN_GB,
  es: SupportedLocale.ES_ES,
  fi: SupportedLocale.FI_FI,
  fr: SupportedLocale.FR_FR,
  hr: SupportedLocale.HR_HR,
  hu: SupportedLocale.HU_HU,
  it: SupportedLocale.IT_IT,
  lt: SupportedLocale.LT_LT,
  nl: SupportedLocale.NL_NL,
  pl: SupportedLocale.PL_PL,
  ro: SupportedLocale.RO_RO,
  ru: SupportedLocale.RU_RU,
  sl: SupportedLocale.SL_SI,
  sv: SupportedLocale.SV_SE,
  zh: SupportedLocale.ZH_CN,
};

export const getLocaleForLanguage = (language: string): SupportedLocale => {
  const defaultLocale = defaultLocalesForLanguages[language];
  return isValidEnvLocale(defaultLocale) ? defaultLocale : SupportedLocale.EN_GB;
};

export const isLocaleSupported = (locale: string) =>
  Object.values(SupportedLocale).includes(locale as SupportedLocale) && isValidEnvLocale(locale);

export const getInitialLocale = (): SupportedLocale => {
  const storedValue = localStorage.getItem('locale') as SupportedLocale;
  return isValidEnvLocale(storedValue)
    ? storedValue
    : getLocaleForLanguage(navigator.language.slice(0, 2));
};

export function getLanguageName(language: string): string {
  const locale = defaultLocalesForLanguages[language];
  return allLocales.get(locale) || language;
}
